import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import { getDoc } from 'firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useSchool } from '../../contexts/SchoolContext';
import { pastoralConfigDoc } from '../../lib/firestore';
import { londonToday } from '../../lib/terms';
import type { BehaviourFeatureId } from '../../types';

/** The pastoral subset — these show the "pastoral routing not set up" nudge. */
const PASTORAL_FEATURES: readonly BehaviourFeatureId[] = ['ool', 'isolation', 'one-way', 'lates'];

/** Common context every behaviour/pastoral feature page passes to its body. */
export type Ctx = { schoolId: string; me: { uid: string; name: string } };

/** schoolId + the current user as {uid,name}, from the signed-in account. */
export function useSchoolAndMe(): Ctx {
  const { appUser } = useAuth();
  return {
    schoolId: appUser?.schoolId ?? '',
    me: { uid: appUser?.id ?? '', name: appUser?.displayName ?? '—' },
  };
}

/** Europe/London school-day date (NOT toISOString(), which is UTC). */
export const today = (): string => londonToday();
export const weekAgoTs = (): Timestamp => Timestamp.fromMillis(Date.now() - 7 * 86_400_000);

export const FOUND_REASONS = ['Arrived late', 'Found in corridor', 'With another member of staff', 'Toilet / medical', 'Sent home', 'Other'];

/**
 * Standard page chrome: centred column, heading + optional subtitle.
 *
 * `wide` is for the pages that carry a table or a chart rather than a form —
 * `actions` puts controls on the heading's own baseline, and is `print:hidden`
 * because a control is not evidence.
 */
export function PageShell({ title, subtitle, actions, wide, children }: {
  readonly title: string;
  readonly subtitle?: React.ReactNode;
  readonly actions?: React.ReactNode;
  readonly wide?: boolean;
  readonly children: React.ReactNode;
}) {
  return (
    <div className={`mx-auto space-y-5 ${wide ? 'max-w-6xl' : 'max-w-4xl'}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2 print:hidden">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

/**
 * Gate a Behaviour & Pastoral feature page on the school's per-feature toggle
 * (schoolPrefs.hiddenBehaviourFeatures). Renders its children only when the
 * feature is switched on, and — for pastoral features that rely on routing —
 * nudges the admin to finish pastoral setup.
 */
export function FeatureGate({ feature, schoolId, children }: { readonly feature: BehaviourFeatureId; readonly schoolId: string; readonly children: React.ReactNode }) {
  const { activeBehaviourFeatures } = useSchool();
  const isPastoral = PASTORAL_FEATURES.includes(feature);
  const [hasConfig, setHasConfig] = useState(true);

  useEffect(() => {
    if (!schoolId || !isPastoral) return;
    let live = true;
    getDoc(pastoralConfigDoc(schoolId)).then((snap) => { if (live) setHasConfig(snap.exists()); }).catch(() => { /* ignore */ });
    return () => { live = false; };
  }, [schoolId, isPastoral]);

  if (!activeBehaviourFeatures.includes(feature)) {
    return (
      <p className="card p-6 text-sm text-gray-500">
        This feature is switched off for your school. A school admin can switch it back on in{' '}
        <Link to="/school/settings/behaviour" className="text-brand-600 hover:underline">Settings › Behaviour &amp; pastoral</Link>.
      </p>
    );
  }

  return (
    <>
      {isPastoral && !hasConfig && (
        <div className="card border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Pastoral routing isn’t set up yet, so alerts and end-of-day chases have nobody to go to.{' '}
          <Link to="/school/settings/behaviour" className="font-medium underline">Set up pastoral routing →</Link>
        </div>
      )}
      {children}
    </>
  );
}

/** "a", "a and b", "a, b and c" — British list punctuation, no Oxford comma. */
export function listInWords(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
